import { useMemo } from 'react';
import {
  getGlobalTextColorStyle,
  getTextColorStyleWithDisplay,
} from '@builder4/shared/utils/styleHelpers';
import type { Color } from '@builder4/shared/types/layout';
import type { StyleColors } from '@builder4/shared/types/styles';

/**
 * Hook to get memoized global text color style that properly detects nested gradient changes.
 * Serializes the color value to ensure React's useMemo detects changes to gradient stops
 * even when the object reference remains the same.
 *
 * @param fontColor - The font color value (Color | string | null | undefined)
 * @param styleColors - StyleColors object for resolving $ref: color references
 * @returns Memoized CSS properties object for text color styling
 */
export function useGlobalTextColorStyle(
  fontColor: Color | string | null | undefined,
  styleColors: StyleColors,
): React.CSSProperties {
  // Serialize fontColor to detect nested changes (e.g., gradient stops)
  // This ensures useMemo recalculates when gradient stops change even if object reference is the same
  const fontColorKey = useMemo(() => (fontColor ? JSON.stringify(fontColor) : ''), [fontColor]);

  return useMemo(
    () => getTextColorStyleWithDisplay(getGlobalTextColorStyle(fontColor, styleColors)),
    [fontColorKey, styleColors, fontColor],
  );
}
